import { create } from 'zustand';
import { AlertPosition, AlertType } from '../../enum/EnumRepository';
import { getAlertMessage } from '../../constants/alertMessages';


export interface StandardAlert {
  type: AlertType | null;
  position: AlertPosition | null;
  content: string;
  callBack?: () => void | Promise<void>;
  cancelCallback?: () => void;
}

interface StandardAlertState {
  queue: StandardAlert[];
  isClosing: boolean;
  isExecuting: boolean;

  enqueueAlert: (alert: StandardAlert) => void;

  setUnchangedAlert: (
    customCallback: () => void | Promise<void>,
    cancelCallback?: () => void,
  ) => void;
  setTextLengthExceededAlert: (maxLength: number) => void;
  setSubscriptionFailedAlert: () => void;

  executeCurrent: () => Promise<void>;
  cancelCurrent: () => void;
  closeCurrent: (time?: number) => void; // FIFO pop
  clearAll: () => void;
}


export const useStandardAlertStore = create<StandardAlertState>((set, get) => {
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelClose = () => {
    if (!closeTimer) return;
    clearTimeout(closeTimer);
    closeTimer = null;
  };

  const enqueue = (alert: StandardAlert) =>
    set((s) => ({ queue: [...s.queue, alert] }));

  const closeCurrent = (time: number = 500) => {
    const { queue, isClosing } = get();
    if (!queue.length || isClosing) return;

    set({ isClosing: true });

    closeTimer = setTimeout(() => {
      closeTimer = null;
      set((s) => ({
        queue: s.queue.slice(1),
        isClosing: false,
        isExecuting: false,
      }));
    }, time);
  };

  const executeCurrent = async () => {
    const { queue, isClosing, isExecuting } = get();
    const currentAlert = queue[0];
    if (!currentAlert || isClosing || isExecuting) return;

    if (!currentAlert.callBack) {
      closeCurrent();
      return;
    }

    set({ isExecuting: true });
    try {
      await currentAlert.callBack();
    } catch (error) {
      console.error('Alert callback failed:', error);
    } finally {
      const currentState = get();
      if (currentState.queue[0] === currentAlert && !currentState.isClosing) {
        closeCurrent();
      }
    }
  };

  const cancelCurrent = () => {
    const { queue, isClosing, isExecuting } = get();
    const currentAlert = queue[0];
    if (!currentAlert || isClosing || isExecuting) return;

    try {
      currentAlert.cancelCallback?.();
    } catch (error) {
      console.error('Alert cancel callback failed:', error);
    } finally {
      if (get().queue[0] === currentAlert) {
        closeCurrent();
      }
    }
  };

  return {
    queue: [],
    isClosing: false,
    isExecuting: false,
    enqueueAlert: enqueue,
    setUnchangedAlert: (customCallback, cancelCallback) =>
      enqueue({
        type: AlertType.CONFIRM,
        position: AlertPosition.TOP,
        content: getAlertMessage('UNSAVED_CHANGES'),
        callBack: customCallback,
        cancelCallback,
      }),
    setTextLengthExceededAlert: (maxLength) =>
      enqueue({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('TEXT_LENGTH_EXCEEDED', { maxLength }),
      }),
    setSubscriptionFailedAlert: () =>
      enqueue({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SUBSCRIPTION_FAILED'),
      }),
    executeCurrent,
    cancelCurrent,
    closeCurrent,
    clearAll: () => {
      cancelClose();
      set({ queue: [], isClosing: false, isExecuting: false });
    },
}
});
